import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Flex,
  Button,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/core';
import { Column } from 'react-table';
import isEqual from 'lodash/isEqual';
import { AnalyticsPage } from '../accountParameter.types';
import { DataTable } from './DataTable';
import { useSettingsDisabled } from '../SettingsDisabledContext';

export const AnalyticsPagesManager: React.FC<{
  value: AnalyticsPage[];
  onChange: (pages: AnalyticsPage[]) => void;
  baselinePages: AnalyticsPage[];
}> = ({ value, onChange, baselinePages }) => {
  const { t } = useTranslation('accountParameter');
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [pageInForm, setPageInForm] = React.useState<AnalyticsPage | null>(null);
  const [originalEngName, setOriginalEngName] = React.useState<string | null>(null);
  const disabled = useSettingsDisabled();

  const handleOpenModalToAdd = () => {
    setPageInForm({ engName: '', engURL: '', frName: '', frURL: '' });
    setIsModalOpen(true);
  };

  const handleOpenModalToEdit = (page: AnalyticsPage) => {
    setPageInForm(page);
    setOriginalEngName(page.engName); // Store the original engName
    setIsModalOpen(true);
  };

  const handleSavePage = () => {
    if (!pageInForm) return;

    let updatedPages;
    if (originalEngName !== null) {
      // If we are in edit mode
      updatedPages = value.map((page) => (page.engName === originalEngName ? pageInForm : page));
    } else {
      // If we are adding a new page
      updatedPages = [...value, pageInForm];
    }

    onChange(updatedPages);
    setIsModalOpen(false);
    setPageInForm(null);
    setOriginalEngName(null); // Reset originalEngName after saving
  };

  const handleRemovePage = (engName: string) => {
    const updatedPages = value.filter((page) => page.engName !== engName);
    onChange(updatedPages);
  };

  const columns = React.useMemo<Column<AnalyticsPage>[]>(
    () => [
      {
        Header: t('analyticsPagesManager.engName') as string,
        accessor: 'engName',
        width: 150,
        Cell: ({ value }) => (
          <Box whiteSpace="normal" wordBreak="break-all">
            {value}
          </Box>
        ),
      },
      {
        Header: t('analyticsPagesManager.engURL') as string,
        accessor: 'engURL',
        width: 300, // Double bigger
        Cell: ({ value }) => (
          <Box whiteSpace="normal" wordBreak="break-all">
            {value}
          </Box>
        ),
      },
      {
        Header: t('analyticsPagesManager.frName') as string,
        accessor: 'frName',
        width: 150,
        Cell: ({ value }) => (
          <Box whiteSpace="normal" wordBreak="break-all">
            {value}
          </Box>
        ),
      },
      {
        Header: t('analyticsPagesManager.frURL') as string,
        accessor: 'frURL',
        width: 300, // Double bigger
        Cell: ({ value }) => (
          <Box whiteSpace="normal" wordBreak="break-all">
            {value}
          </Box>
        ),
      },
      {
        Header: t('analyticsPagesManager.actions') as string,
        id: 'actions',
        disableSortBy: true,
        width: 150, // Adjust width for actions column
        Cell: ({ row }: any) => (
          <Flex>
            <Button size="sm" mr={2} onClick={() => handleOpenModalToEdit(row.original)} disabled={disabled}>
              {t('analyticsPagesManager.edit')}
            </Button>
            <Button size="sm" colorScheme="red" onClick={() => handleRemovePage(row.original.engName)} disabled={disabled}>
              {t('analyticsPagesManager.remove')}
            </Button>
          </Flex>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value, onChange],
  );

  const isSaveDisabled = !pageInForm;

  return (
    <Box p={4} mt={4} borderWidth="1px" borderRadius="md">
      <Flex justify="space-between" align="center" mb={4}>
        <Heading as="h5" size="sm">
          {t('analyticsPagesManager.analyticsPages')}
        </Heading>
        <Button colorScheme="blue" onClick={handleOpenModalToAdd} disabled={disabled}>
          {t('analyticsPagesManager.addPage')}
        </Button>
      </Flex>
      <DataTable
        columns={columns}
        data={value}
        getRowProps={(row: any) => {
          const current = row.original as AnalyticsPage;
          const baseline = baselinePages.find((p) => p.engName === current.engName);
          const changed = !baseline || !isEqual(baseline, current);
          return { className: changed ? 'dirty' : '' };
        }}
      />

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {pageInForm && value.some((p) => p.engName === pageInForm.engName)
              ? t('analyticsPagesManager.editAnalyticsPage')
              : t('analyticsPagesManager.addNewAnalyticsPage')}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {pageInForm && (
              <>
                <FormControl>
                  <FormLabel>{t('analyticsPagesManager.engName')}</FormLabel>
                  <Input value={pageInForm.engName} onChange={(e) => setPageInForm({ ...pageInForm, engName: e.target.value })} />
                </FormControl>
                <FormControl mt={4}>
                  <FormLabel>{t('analyticsPagesManager.engURL')}</FormLabel>
                  <Input value={pageInForm.engURL} onChange={(e) => setPageInForm({ ...pageInForm, engURL: e.target.value })} />
                </FormControl>
                <FormControl mt={4}>
                  <FormLabel>{t('analyticsPagesManager.frName')}</FormLabel>
                  <Input value={pageInForm.frName} onChange={(e) => setPageInForm({ ...pageInForm, frName: e.target.value })} />
                </FormControl>
                <FormControl mt={4}>
                  <FormLabel>{t('analyticsPagesManager.frURL')}</FormLabel>
                  <Input value={pageInForm.frURL} onChange={(e) => setPageInForm({ ...pageInForm, frURL: e.target.value })} />
                </FormControl>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setIsModalOpen(false)}>
              {t('analyticsPagesManager.cancel')}
            </Button>
            <Button colorScheme="blue" onClick={handleSavePage} isDisabled={isSaveDisabled}>
              {t('analyticsPagesManager.save')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};
